import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { PersonaTipoUtenteEnum } from '../../api/generated';
import { env } from '../../env';
import walletPageMessages from '../../messages/wallet/wallet-page.messages';
import { TabItem } from '../../model';
import { WalletSections } from '../../model/wallet/wallet-sections';
import { HomeState, PirBoxState, retrieveHomeWalletSummary } from '../../store';
import {
  dossierSliceActions,
  getDossierFilter,
  retrieveDossier,
  selectDossierState,
} from '../../store/data/dossier';
import {
  getSelectedWalletSection,
  myWalletSliceActions,
  sharedAccountsSliceActions,
  walletCommonSliceActions,
} from '../../store/features/wallet';
import { useAppDispatch, useAppSelector } from '../redux';

export interface WalletLogic {
  sections: TabItem<WalletSections>[];
  selectedSection: WalletSections;

  onSelectedSection: (selectedSection: WalletSections) => void;
}

export const useWalletLogic = (): WalletLogic => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const selectedSection: WalletSections = useAppSelector(
    getSelectedWalletSection
  );

  const token: string = useAppSelector((state) => state.data?.user?.token);
  const { myWallet } = useAppSelector(
    (state) => state.features.home as HomeState
  );

  const userType: PersonaTipoUtenteEnum = useAppSelector(
    (state) => state.data?.user?.userType
  );

  const { productsSummary } = useAppSelector(
    (state) => state.features.pirbox as PirBoxState
  );

  const { dossierFilter, dossierFilterRequestStatus, defaultDossier } =
    useAppSelector(selectDossierState);

  const [dossierNumberCallingRequest, setDossierNumberCallingRequest] =
    useState(0);
  const [walletNumberCallingRequest, setWalletNumberCallingRequest] =
    useState(0);

  const sections = useMemo<TabItem<WalletSections>[]>(() => {
    const tabs: TabItem<WalletSections>[] = [
      {
        type: 'MY_WALLET',
        label: formatMessage(walletPageMessages.myWalletLabel),
      },
    ];
    if (userType === PersonaTipoUtenteEnum.Pf) {
      if (
        getDossierFilter(dossierFilter, 'SHARED_ACCOUNTS')?.dossier?.length > 0
      ) {
        tabs.splice(1, 0, {
          type: 'SHARED_ACCOUNTS',
          label: formatMessage(walletPageMessages.jointRelationshipsLabel),
        });
      }
      if (Array.isArray(productsSummary) && productsSummary.length > 0) {
        tabs.splice(2, 0, {
          type: 'PIR_BOX',
          label: formatMessage(walletPageMessages.pirBoxLabel),
        });
      }
    }

    return tabs;
  }, [formatMessage, userType, dossierFilter, productsSummary]);

  useEffect(() => {
    dispatch(dossierSliceActions.setDossierFilter(defaultDossier));
  }, [defaultDossier, dispatch]);

  useEffect(() => {
    if (
      token &&
      walletNumberCallingRequest === 0 &&
      dossierFilter &&
      dossierFilter.dossier.length > 0 &&
      (!myWallet || !myWallet.value) &&
      myWallet?.requestStatus !== 'loading'
    ) {
      setWalletNumberCallingRequest(walletNumberCallingRequest + 1);
      dispatch(retrieveHomeWalletSummary());
    }
  }, [dispatch, dossierFilter, myWallet, token, walletNumberCallingRequest]);

  useEffect(() => {
    if (
      token &&
      dossierNumberCallingRequest === 0 &&
      !dossierFilter &&
      dossierFilterRequestStatus !== 'loading'
    ) {
      setDossierNumberCallingRequest(dossierNumberCallingRequest + 1);
      dispatch(retrieveDossier());
    }
  }, [
    dispatch,
    setDossierNumberCallingRequest,
    dossierFilter,
    dossierFilterRequestStatus,
    dossierNumberCallingRequest,
    token,
  ]);

  useEffect(() => {
    return () => {
      // Mobile module has its own cleanup logic due to the existence of detail page
      if (env.appModule === 'web') {
        dispatch(dossierSliceActions.resetFilter());
        dispatch(myWalletSliceActions.resetWalletState());
        dispatch(sharedAccountsSliceActions.resetWalletState());
        dispatch(walletCommonSliceActions.resetWalletCommonState());
      }
    };
  }, [dispatch]);

  const onSelectedSection = useCallback(
    (sectionType: WalletSections) => {
      dispatch(walletCommonSliceActions.setSelectedWalletSection(sectionType));
    },
    [dispatch]
  );

  return {
    sections,
    selectedSection,
    onSelectedSection,
  };
};
